import {Hero} from '@primer/react-brand'
import styled from 'styled-components'

import type {PrimerComponentHero} from '../../../schemas/contentful/contentTypes/primerComponentHero'
import {ContentfulAppStoreButtonGroup} from '../ContentfulAppStoreButtonGroup/ContentfulAppStoreButtonGroup'
import {getAnalytics} from '../../../lib/utils/analytics'

export type ContentfulHeroProps = {
  component: PrimerComponentHero

  'data-hpc'?: boolean
}

const StyledVideoContainer = styled.div`
  /* stylelint-disable-next-line primer/borders */
  border-radius: var(--brand-borderRadius-large);
  overflow: hidden;
  width: 100%;
  margin-top: var(--base-size-40);
  position: relative;
  padding-bottom: 56%;

  @media screen and (min-width: 34rem) {
    margin-top: var(--base-size-80);
  }
`
const StyledContentfulHeroContainer = styled.div`
  padding-bottom: var(--base-size-96);

  @media screen and (min-width: 34rem) {
    padding-bottom: var(--base-size-128);
  }
`
export function ContentfulHero({component, ...props}: ContentfulHeroProps) {
  const hasTrailingComponent =
    component.fields.trailingComponent !== undefined && component.fields.trailingComponent.length > 0

  return (
    <StyledContentfulHeroContainer>
      <Hero
        data-hpc={props['data-hpc']}
        align={component.fields.align}
        trailingComponent={
          hasTrailingComponent
            ? () => (
                <ContentfulAppStoreButtonGroup components={component.fields.trailingComponent} analyticsLabel="hero" />
              )
            : undefined
        }
        className="pb-0"
      >
        {component.fields.label && <Hero.Label>{component.fields.label}</Hero.Label>}

        <Hero.Heading>{component.fields.heading}</Hero.Heading>

        {component.fields.description ? <Hero.Description>{component.fields.description}</Hero.Description> : null}

        {!hasTrailingComponent && component.fields.callToActionPrimary !== undefined && (
          <Hero.PrimaryAction
            href={component.fields.callToActionPrimary.fields.href}
            {...getAnalytics({
              action: component.fields.callToActionPrimary.fields.text,
              labels: [{key: 'label', value: 'hero'}],
              fullstoryDataRef: `hero-primary-action-${component.fields.callToActionPrimary.sys.id}`,
            })}
          >
            {component.fields.callToActionPrimary.fields.text}
          </Hero.PrimaryAction>
        )}

        {!hasTrailingComponent && component.fields.callToActionSecondary !== undefined && (
          <Hero.SecondaryAction
            href={component.fields.callToActionSecondary.fields.href}
            {...getAnalytics({
              action: component.fields.callToActionSecondary.fields.text,
              labels: [{key: 'label', value: 'hero'}],
              fullstoryDataRef: `hero-secondary-action-${component.fields.callToActionSecondary.sys.id}`,
            })}
          >
            {component.fields.callToActionSecondary.fields.text}
          </Hero.SecondaryAction>
        )}
        {component.fields.image !== undefined ? (
          <Hero.Image
            position={component.fields.imagePosition === 'Inline' ? 'inline-end' : 'block-end'}
            src={component.fields.image.fields.file.url}
            alt={component.fields.image.fields.description || ''}
          />
        ) : null}
      </Hero>
      {component.fields.videoSrc !== undefined && !component.fields.image && (
        <StyledVideoContainer>
          <iframe
            role="application"
            className="border-0 width-full height-full position-absolute top-0 left-0"
            src={component.fields.videoSrc}
            title={component.fields.heading}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </StyledVideoContainer>
      )}
    </StyledContentfulHeroContainer>
  )
}

try{ ContentfulHero.displayName ||= 'ContentfulHero' } catch {}